export type QueueGrouping = 'location' | 'calls';

export type QueueSort = { by: string; dir: 'asc' | 'desc' };

export type EtaFilter = 'any' | 'has' | 'none';

export type AttentionTabKey =
  | 'new'
  | 'overdue'
  | 'due_soon'
  | 'needs_dispatch'
  | 'ready_to_schedule'
  | 'waiting_on_quote'
  | 'ready_to_bill'
  | 'all_active'
  | 'open_on_me'
  | 'incomplete'
  | 'sync_issues';

export type QueueFilters = {
  departmentIds: number[];
  customerIds: number[];
  propertyIds: number[];
  statuses: string[];
  /** ServiceChannel primary statuses (sc_status_primary), separate from the job's own. */
  scStatuses: string[];
  /** Priority levels (priority_level): the leading P<n> of the label, '' for a call without one. */
  priorityLevels: string[];
  search: string;
  eta: EtaFilter;
  /** 0 = everyone. Matched inside the comma-separated technician_assigned list. */
  technicianId: number;
};

export type QueueViewConfig = QueueFilters & {
  tab: AttentionTabKey;
  grouping: QueueGrouping;
};

export const EMPTY_FILTERS: QueueFilters = {
  departmentIds: [],
  customerIds: [],
  propertyIds: [],
  statuses: [],
  scStatuses: [],
  priorityLevels: [],
  search: '',
  eta: 'any',
  technicianId: 0,
};

export const DEFAULT_VIEW_CONFIG: QueueViewConfig = {
  ...EMPTY_FILTERS,
  tab: 'all_active',
  grouping: 'location',
};

export const COMMERCIAL_DEPARTMENTS: { id: number; name: string }[] = [
  { id: 17, name: 'Commercial Refrigeration' },
  { id: 18, name: 'Commercial HVAC' },
  { id: 15, name: 'Electrical' },
  { id: 34, name: 'Plumbing' },
];

export const QUEUE_STATUSES = [
  'Requested',
  'Confirmed',
  'Enroute',
  'On Call',
  'Delayed',
  'Incomplete',
  'Part on Order',
  'Part Received',
  'Pend Sched',
  'Completed',
  'Canceled',
] as const;

/**
 * ServiceChannel's primary work order statuses, as it sends them. A fixed list:
 * the extended statuses are open-ended, so the filter is primary-only.
 */
export const SC_QUEUE_STATUSES: { value: string; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN PROGRESS', label: 'In Progress' },
  { value: 'ON HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
];

/**
 * Priority/SLA levels, matched on the view's priority_level: the leading P<n>
 * of the customer's priority label, so "P1 - 4 Hours" and "P1 (Emergency)"
 * both read as P1 and the filter works across customers whose labels differ.
 * '' is a call whose label has no P rating, or no priority at all (86bckgt7j).
 * P6 and P7 are ServiceChannel's scheduled maintenance and scheduled repair
 * levels, e.g. "P6 Scheduled Maintenance (30 Days)" (86bct3t1h).
 */
export const PRIORITY_LEVELS: { value: string; label: string }[] = [
  { value: 'P1', label: 'P1' },
  { value: 'P2', label: 'P2' },
  { value: 'P3', label: 'P3' },
  { value: 'P4', label: 'P4' },
  { value: 'P5', label: 'P5' },
  { value: 'P6', label: 'P6' },
  { value: 'P7', label: 'P7' },
  { value: '', label: 'No priority' },
];

export const ATTENTION_TABS: { key: AttentionTabKey; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_soon', label: 'Due Soon' },
  // Ops mark both "new, not yet placed" and "parts received" as Pend Sched, so
  // the tab is named for the status it shows. The key stays: saved views store it.
  { key: 'needs_dispatch', label: 'Pend Sched' },
  { key: 'ready_to_schedule', label: 'Ready to Schedule' },
  { key: 'waiting_on_quote', label: 'Waiting on Quote' },
  { key: 'ready_to_bill', label: 'Ready to Bill' },
  { key: 'all_active', label: 'All Active' },
  // Hidden while its count is 0; see AttentionTabs.
  { key: 'sync_issues', label: 'Sync Issues' },
];

/** The office's triage tabs on phones, in the order they're used. */
export const MOBILE_TRIAGE_TABS: { key: AttentionTabKey; label: string }[] = [
  { key: 'needs_dispatch', label: 'Pend Sched' },
  { key: 'new', label: 'New' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'all_active', label: 'All' },
];

/** The rest of the tabs, under More on phones. */
export const MOBILE_MORE_TABS: { key: AttentionTabKey; label: string }[] = [
  { key: 'due_soon', label: 'Due soon' },
  { key: 'ready_to_schedule', label: 'Ready to schedule' },
  { key: 'waiting_on_quote', label: 'Waiting on quote' },
  { key: 'ready_to_bill', label: 'Ready to bill' },
  { key: 'incomplete', label: 'Incomplete' },
];

/** Tabs whose name is the status, so a row inside them doesn't repeat it. */
export const STATUS_DEFINED_TABS: AttentionTabKey[] = [
  'needs_dispatch',
  'ready_to_schedule',
  'ready_to_bill',
  'incomplete',
];

export const mobileTabLabel = (key: AttentionTabKey) =>
  [...MOBILE_TRIAGE_TABS, ...MOBILE_MORE_TABS].find((tab) => tab.key === key)?.label ?? 'All';

/** Tabs only phones offer; desktop chips have no slot for them. */
export const MOBILE_ONLY_TABS: AttentionTabKey[] = ['open_on_me', 'incomplete'];

export const SAVED_VIEW_TYPE_COMMERCIAL_QUEUE = 1;

export type ViewCustomer = {
  id: number;
  label: string;
  /** Display + re-expansion on edit; propertyIds stays concrete either way. */
  allProperties: boolean;
  propertyIds: number[];
};

export type ViewScope = { customers: ViewCustomer[] };

/**
 * The unscoped view: every commercial job, no customer filter.
 *
 * Saved views are customer lists expanded when the view is saved, so a customer
 * added afterwards is not in any of them and its jobs cannot be reached from
 * the queue at all. That is not hypothetical — ServiceChannel raises work
 * orders for a subscriber on its own, and the job it creates is invisible here
 * until someone edits a view.
 *
 * Deliberately not the default, which stays the first saved view. This is a
 * sentinel rather than 0 precisely to keep the two apart: 0 means "nothing
 * chosen yet" and resolves to that first view, while this is a choice, and
 * persists like any other.
 */
export const ALL_CUSTOMERS_VIEW_ID = -1;
